<template>
  <v-container>
    <router-link
      class="subtitle-1 grey-navy--text font-weight-bold text-decoration-none"
      to="/coaches"
    >
      <v-icon small class="pb-1"> keyboard_backspace </v-icon> Coaches
    </router-link>
    <v-row>
      <v-col cols="12">
        <h2 class="mb-2">
          Historical view for {{ coach.firstName }} {{ coach.lastName }}
        </h2>

        <highcharts v-if="chartData" :options="chartData"></highcharts>

        <v-data-table
          :headers="summaryTable.headers"
          :items="summaryTable.data"
          :hide-default-footer="true"
          disable-pagination
          :disable-sort="true"
          class="mt-10"
        >
          <template v-slot:item="props">
            <tr>
              <td v-for="(prop, key) in props.item" :key="key">
                <span
                  :class="
                    getSummaryMetricClass(getNumberForLabel(props.item[key]))
                  "
                  v-if="isNaN(props.item[key]) && key != 'type'"
                  >{{ props.item[key] }}</span
                >
                <span v-else>{{ props.item[key] }}</span>
              </td>
            </tr>
          </template>
        </v-data-table>
        <h2 class="mt-10 mb-2">Detailed view</h2>
        <v-data-table
          :headers="detailTable.headers"
          :items="detailTable.data"
          :hide-default-footer="true"
          disable-pagination
          :disable-sort="true"
          group-by="client"
        >
          <template v-slot:item="props">
            <tr>
              <td v-for="(prop, key) in removeClient(props.item)" :key="key">
                <span :class="getExerciseClass(props.item[key], prop, key)">{{
                  props.item[key]
                }}</span>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>
<script>
import MetricMixin from "@/components/shared/mixins/MetricMixin";
import {
  COACH_QUERY,
  COACH_SUMMARY_METRIC_HISTORY,
  COACH_METRIC_HISTORY,
} from "@/constants/graphql/coach/coaching";

export default {
  name: "CoachHistory",
  mixins: [MetricMixin],
  props: {
    coachId: {
      required: true,
    },
  },
  data() {
    return {
      coach: {
        firstName: "",
        lastName: "",
      },
      chartData: null,
      metricNumberHistory: [],
    };
  },
  methods: {
    removeClient(items) {
      // remove client key so that the grouped data table isn't out of whack by one.
      delete items["client"];
      return items;
    },
    getExerciseClass(val, prop, key) {
      if (val === true) {
        return "metric-green";
      }
      if (val === false) {
        return "metric-red";
      }
      return "";
    },
  },
  apollo: {
    coachMetricHistory: {
      query: COACH_METRIC_HISTORY,
      variables() {
        return {
          dateFrom: this.$moment()
            .subtract(this.startDays, "days")
            .format("YYYY-MM-DD"),
          coachId: parseInt(this.coachId),
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data) {
          this.detailTable.data = [];
          response.data.coachMetricHistory.forEach((row) => {
            let client = row.fullName;
            let metricNumber = { type: "Metric", client: client };
            let sevenDayAvg = { type: "7 day avg", client: client };
            let exerciseLogged = { type: "Exercise logged", client: client };
            let numberMessages = { type: "# messages", client: client };
            let numberNutritionPics = {
              type: "# nutrition pics",
              client: client,
            };

            this.dayList.forEach((date) => {
              // if we weren't doing our own html in the data table with :slot, then this padding with '-' would not have been required.
              let metric = row.metricList.find((o) => o.metricDate == date);
              if (metric) {
                let metricDate = this.$moment(metric.metricDate).format(
                  "DD-MM"
                );
                metricNumber[metricDate] = metric.metricNumber;
                sevenDayAvg[metricDate] = metric.sevenDayAverageNumber;
                exerciseLogged[metricDate] = metric.exerciseLogged;
                numberMessages[metricDate] = metric.numberMessages;
                numberNutritionPics[metricDate] = metric.numberNutritionPics;
              } else {
                let metricDate = this.$moment(date).format("DD-MM");
                metricNumber[metricDate] = "-";
                sevenDayAvg[metricDate] = "-";
                exerciseLogged[metricDate] = "-";
                numberMessages[metricDate] = "-";
                numberNutritionPics[metricDate] = "-";
              }
            });
            this.detailTable.data.push(metricNumber);
            this.detailTable.data.push(sevenDayAvg);
            this.detailTable.data.push(exerciseLogged);
            this.detailTable.data.push(numberMessages);
            this.detailTable.data.push(numberNutritionPics);
          });
        }
      },
    },
    coach: {
      query: COACH_QUERY,
      variables() {
        return {
          id: this.coachId,
        };
      },
      fetchPolicy: "cache-and-network",
    },
    coachSummaryMetricHistory: {
      query: COACH_SUMMARY_METRIC_HISTORY,
      variables() {
        return {
          metricDate_Gte: this.$moment()
            .subtract(this.startDays, "days")
            .format("YYYY-MM-DD"),
          coachId: parseInt(this.coachId),
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data) {
          this.summaryTable.data = [];
          this.metricNumberHistory = [];
          let averageLabelRow = { type: "" };
          let averageNumber = { type: "Metric #" };
          let numberClientsNoPics = { type: "# with no pics" };
          let numberClientsNoWorkout = { type: "# not logged workout" };
          let numberClientsOver50 = { type: "# clients > 50" };
          response.data.coachSummaryMetricHistory.edges.forEach((row) => {
            let metricDate = this.$moment(row.node.metricDate).format("DD-MM");
            averageLabelRow[metricDate] = row.node.averageLabel;

            averageNumber[metricDate] = row.node.averageNumber;
            this.metricNumberHistory.push(row.node.averageNumber);

            numberClientsNoPics[metricDate] = row.node.numberClientsNoPics;
            numberClientsNoWorkout[metricDate] =
              row.node.numberClientsNoWorkout;
            numberClientsOver50[metricDate] = row.node.numberClientsOver50;
          });
          this.summaryTable.data.push(averageLabelRow);
          this.summaryTable.data.push(averageNumber);
          this.summaryTable.data.push(numberClientsNoPics);
          this.summaryTable.data.push(numberClientsNoWorkout);
          this.summaryTable.data.push(numberClientsOver50);

          this.chartData = {
            title: {
              text: "",
            },
            plotOptions: {
              series: {
                pointPlacement: "on",
              },
            },
            credits: {
              enabled: false,
            },
            chart: {
              type: "area",
            },
            yAxis: {
              title: {
                text: "Metric #",
              },
              plotLines: [
                {
                  color: "red",
                  width: 1,
                  value: 25,
                },
                {
                  color: "yellow",
                  width: 1,
                  value: 50,
                },
                {
                  color: "green",
                  width: 1,
                  value: 75,
                },
              ],
            },
            xAxis: {
              categories: this.shortDates,
            },
            series: [
              {
                name: "Metric #",
                data: this.metricNumberHistory,
              },
            ],
          };
        }
      },
    },
  },
};
</script>
